// Estado "ya vi este onboarding" del usuario, persistido SOLO en la DB
// (columna `profiles.onboarding_seen` jsonb, un objeto { "welcome": true, ... }).
//
// Es POR CUENTA y cross-device. Antes había un espejo en localStorage, pero eso
// era POR NAVEGADOR: si dos cuentas usaban el mismo navegador, la segunda NO veía
// el onboarding (quedaba marcado el flag de la primera). Por eso ahora la única
// fuente de verdad es la DB → "una vez por cuenta", consistente en cualquier
// dispositivo, y resetear es solo tocar la DB.
//
// `profiles` hoy NO tiene RLS (ver memoria de seguridad), así que el update desde
// el cliente funciona. Si en el futuro se activa RLS, hace falta una política de
// UPDATE de la propia fila.

import { supabase } from "./supabase";

// Llaves de los onboardings, tal como viven dentro del jsonb.
export type OnboardingKey =
  | "welcome" // modal de bienvenida (pantalla de proyectos)
  | "tour" // tour guiado principal del editor
  | "ctx_transitions"
  | "ctx_dialog"
  | "ctx_video"
  | "ctx_audio"
  | "ctx_files"
  | "estudio_from_historial" // popup al tocar "Editar con estudio" en el historial (1ra vez)
  | "tiendanube_banner" // banner "Conectá tu Tienda Nube" del estudio (post "Mejorá tus fotos")
  // ── Configuración de cuenta paga (tarjeta de home con los 2 pasos) ──
  // Se marca la PRIMERA vez que el usuario ve la tarjeta con algún paso
  // pendiente. Es la condición para poder felicitarlo después: sin esto, quien
  // ya tenía todo hecho antes de que existiera la tarjeta abriría la home con un
  // "¡configuración completa!" por algo que nunca hizo.
  | "setup_pago_en_curso"
  // La felicitación ya se mostró: no vuelve nunca más.
  | "setup_pago_ok"
  // El usuario apretó "Ahora no" en la tarea de Tienda Nube: la tarea deja de
  // pedir algo y la tarjeta la muestra como omitida. Es POR CUENTA y no expira —
  // volver a pedirla en la próxima carga sería no haber escuchado el "ahora no".
  // Se reactiva sola si la tienda termina conectada por otro lado (Mi marca):
  // ahí la tarea está HECHA y el flag deja de mirarse.
  | "setup_tiendanube_omitido"
  | "probar_escritorio_banner" // banner "Probá Atlas en tu computadora" (sólo teléfono, post encuesta)
  | "capacitacion_ultra_banner" // banner "Agendá tu llamada de capacitación" (planes Pro y Ultra; la clave conserva el nombre viejo); lo marcan "No me interesa" Y "Agendar"
  // ── Recorridos guiados de Tienda online (ADR-2026-10-01-recorridos-de-tienda-online) ──
  | "tienda_tour_inicio" // lista vacía: señala "Nuevo lanzamiento"
  | "tienda_tour_destino" // modal de crear: el destino decide la ficha y no se cambia después
  | "tienda_tour_detalle" // lanzamiento con productos: tabla, lote, Modificar, Categorías, Publicar
  | "tienda_tour_vacio" // lanzamiento sin productos: "Elegir creaciones"
  | "tienda_tour_ficha" // ficha de un producto: estado, IA, agregar/reemplazar, fotos
  // ── Anuncios de funcionalidad nueva (ADR-2026-08-31-anuncios-de-funcionalidad) ──
  // Abierto a propósito: las llaves las define el catálogo de `lib/anuncios.ts`, no
  // esta lista. Un anuncio nuevo es una entrada en el catálogo y nada más — si cada
  // uno tuviera que sumarse acá también, tarde o temprano alguien agrega el anuncio,
  // se olvida de la llave y el "ya lo vi" deja de persistir sin que nada falle.
  // El prefijo evita que un id de anuncio pise una llave de onboarding.
  | `anuncio:${string}`;

// Cache en memoria del jsonb + a qué usuario corresponde.
let cache: Record<string, boolean> | null = null;
let cachedUserId: string | null = null;

/**
 * Carga `onboarding_seen` del usuario UNA vez y lo cachea. Idempotente: si ya
 * está cacheado para ese usuario, no vuelve a pegarle a la DB. Llamar temprano
 * (al montar el editor / la pantalla de proyectos) y AWAITear antes de decidir
 * si se muestra algo, para que `isSeen` tenga la verdad de la DB.
 */
export async function loadOnboardingSeen(userId: string): Promise<void> {
  if (cache && cachedUserId === userId) return;
  cachedUserId = userId;
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("onboarding_seen")
      .eq("id", userId)
      .single();
    if (error) {
      cache = {};
      return;
    }
    const raw = (data as { onboarding_seen?: unknown } | null)?.onboarding_seen;
    cache =
      raw && typeof raw === "object"
        ? { ...(raw as Record<string, boolean>) }
        : {};
  } catch {
    cache = {};
  }
}

/**
 * ¿El usuario ya vio este onboarding? Mira SOLO la DB (el cache cargado por
 * `loadOnboardingSeen`). Si el cache todavía no cargó, devuelve `false` — los
 * callers que importan (welcome, tour) hacen `await loadOnboardingSeen` antes de
 * chequear, y los tips contextuales corren después de que el editor ya lo cargó.
 */
export function isSeen(key: OnboardingKey): boolean {
  return !!(cache && cache[key] === true);
}

/**
 * Marca un onboarding como visto: cache (en memoria, para esta sesión) + DB. El
 * write a la DB es read-modify-write (trae el jsonb actual, mergea la llave y lo
 * guarda) para NO pisar otras llaves; es fire-and-forget — si falla, se reintenta
 * la próxima vez. Sin `userId` no persiste (solo cache de sesión).
 */
export function markSeen(key: OnboardingKey, userId: string | null): void {
  if (!cache) cache = {};
  cache[key] = true;
  if (!userId) return;
  void (async () => {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_seen")
        .eq("id", userId)
        .single();
      const current = (data as { onboarding_seen?: unknown } | null)
        ?.onboarding_seen;
      const merged: Record<string, boolean> = {
        ...(current && typeof current === "object"
          ? (current as Record<string, boolean>)
          : {}),
        [key]: true,
      };
      cache = merged;
      await supabase
        .from("profiles")
        .update({ onboarding_seen: merged })
        .eq("id", userId);
    } catch {
      /* el cache en memoria ya quedó marcado para esta sesión */
    }
  })();
}
